"use client";
import { forwardRef, useCallback } from "react"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

import { useTableDeleteRowColumn } from "@/tiptap/nodes/table-node/ui/table-delete-row-column-button"

import { Button } from "@/tiptap/ui-primitive/button"

/**
 * Button component for deleting a table row/column in a Tiptap editor.
 *
 * For custom button implementations, use the `useTableDeleteRowColumn` hook instead.
 */
export const TableDeleteRowColumnButton = forwardRef((
  {
    editor: providedEditor,
    index,
    orientation,
    tablePos,
    hideWhenUnavailable = false,
    onDeleted,
    text,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const { isVisible, handleDelete, label, canDeleteRowColumn, Icon } =
    useTableDeleteRowColumn({
      editor,
      index,
      orientation,
      tablePos,
      hideWhenUnavailable,
      onDeleted,
    })

  const handleClick = useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleDelete()
  }, [handleDelete, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type="button"
      disabled={!canDeleteRowColumn}
      variant="ghost"
      data-active-state="off"
      data-disabled={!canDeleteRowColumn}
      role="button"
      tabIndex={-1}
      aria-label={label}
      aria-pressed={false}
      tooltip={label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}>
      {children ?? (
        <>
          <Icon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
        </>
      )}
    </Button>
  );
})

TableDeleteRowColumnButton.displayName = "TableDeleteRowColumnButton"
